import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { get } from 'lodash';

import config from './config';

// Split out of msal-utils.js so it has NO top-level await: msal-utils.js creates the shared MSAL
// instance with `await PublicClientApplication.createPublicClientApplication(...)` at module scope
// (see its own comment), which Jest 27 (react-scripts 5) cannot parse. Every test that needs
// msal-utils already works around that with an explicit `jest.mock('app/msal-utils', () => ({...}))`
// factory (see src/test-utils/render-with-secure-store.tsx's comment) - this module lets the actual
// token-refresh logic below be unit-tested directly against a plain mock instance (msal-token.test.js),
// without going through that factory or PublicClientApplication.createPublicClientApplication at all.

const ID_TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

// acquireTokenSilent's cache-hit decision is based on the ACCESS token's expiry only (see MSAL
// 4.12's SilentFlowClient), so it can happily hand back a cached response whose ID token is itself
// already expired or about to be - lis-front sends the ID token as the API's Bearer token, so that
// silently reintroduces the ~1h-old-token problem this module exists to fix.
const isIdTokenNearExpiry = (idTokenClaims) => {
  const exp = get(idTokenClaims, 'exp');
  return typeof exp === 'number' && exp * 1000 - Date.now() < ID_TOKEN_REFRESH_MARGIN_MS;
};

// True once the token has actually passed its exp, not just entered the near-expiry margin above.
const isExpired = (idTokenClaims) => {
  const exp = get(idTokenClaims, 'exp');
  return typeof exp === 'number' && exp * 1000 - Date.now() < 0;
};

const getAccount = (instance) => instance.getActiveAccount() || instance.getAllAccounts()[0] || null;

// At most one forced-refresh acquireTokenSilent call in flight per MSAL instance at a time, shared
// by every concurrent caller - so e.g. several dashboard widgets discovering a near-expiry token (or
// each retrying their own 401) at the same moment force exactly one network round-trip between them,
// not one each.
const inFlightForcedRefreshes = new WeakMap();

const acquireTokenSilent = (instance, tokenRequest, forceRefresh) => {
  if (!forceRefresh) {
    return instance.acquireTokenSilent({ ...tokenRequest, forceRefresh: false });
  }

  const existing = inFlightForcedRefreshes.get(instance);
  if (existing) {
    return existing;
  }

  const forced = instance.acquireTokenSilent({ ...tokenRequest, forceRefresh: true });
  inFlightForcedRefreshes.set(instance, forced);
  forced.catch(() => {}).finally(() => {
    if (inFlightForcedRefreshes.get(instance) === forced) {
      inFlightForcedRefreshes.delete(instance);
    }
  });
  return forced;
};

// After the SPA's cached refresh token expires (24h), acquireTokenSilent falls back to a hidden
// iframe and, if that iframe doesn't respond within its own ~10s timeout, rejects with a
// BrowserAuthError - "monitor_window_timeout" in msal-browser 4.12, defensively also matching
// "timed_out" in case a future version (or a plain network timeout) surfaces under that code -
// rather than InteractionRequiredAuthError. Ported from lis-paasla's fix/task-123-kiosk-token
// (commit afb7398); without treating this the same as interaction-required, a 401 retry (or any
// other forced refresh) would reject with a raw, unrecognised error instead of ever reaching a
// sign-in redirect.
const SILENT_RENEW_TIMEOUT_CODES = ['monitor_window_timeout', 'timed_out'];
const isSilentRenewTimeout = (error) =>
  Boolean(error) && error.name === 'BrowserAuthError' && SILENT_RENEW_TIMEOUT_CODES.includes(error.errorCode);

// At most one interactive redirect per LOGIN_REDIRECT_GUARD_WINDOW_MS, tracked in sessionStorage:
// if silent renewal starts failing for every request at once (e.g. several dashboard widgets each
// hitting a 401 together), this stops each of them from starting its own redirect.
const LOGIN_REDIRECT_GUARD_KEY = 'luba.lastLoginRedirectAt';
const LOGIN_REDIRECT_GUARD_WINDOW_MS = 5 * 60 * 1000;

const canStartLoginRedirect = () => {
  try {
    const lastRedirectAt = Number(window.sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY));
    if (!lastRedirectAt) {
      return true;
    }
    const elapsedMs = Date.now() - lastRedirectAt;
    // A negative elapsed value means the system clock moved backwards since the last redirect was
    // recorded (e.g. an NTP correction); treat that as "the guard window has passed" instead of
    // blocking redirects until real time catches up with the stored (bogus, future) timestamp.
    return elapsedMs < 0 || elapsedMs >= LOGIN_REDIRECT_GUARD_WINDOW_MS;
  } catch (error) {
    // sessionStorage can throw (e.g. disabled/private browsing); don't let that block a real redirect.
    return true;
  }
};

const markLoginRedirectStarted = () => {
  try {
    window.sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(Date.now()));
  } catch (error) {
    // ignore, see canStartLoginRedirect
  }
};

// Starts an interactive login redirect on `instance`, throttled by canStartLoginRedirect(). Hints
// the current account (if any) so the user isn't asked to pick an account again. Returns whether a
// redirect was actually started.
const startThrottledLoginRedirect = async (instance) => {
  if (!canStartLoginRedirect()) {
    return false;
  }

  const account = getAccount(instance);
  markLoginRedirectStarted();
  await instance.loginRedirect({
    scopes: config.AUTH.loginRequest.scopes,
    ...(account ? { account, loginHint: account.username } : {}),
  });
  return true;
};

// Attempts a throttled login redirect and always resolves null - keeping this module's
// null-return contract even when the redirect itself fails (logged, never left unhandled).
const startThrottledLoginRedirectAndReturnNull = async (instance) => {
  try {
    await startThrottledLoginRedirect(instance);
  } catch (redirectError) {
    console.error('msal-token: login redirect failed', redirectError);
  }
  return null;
};

// Handles a failure of the INITIAL (non-forced) acquireTokenSilent call. Keeps this module's
// established contract for anything unrecognised: only InteractionRequiredAuthError and a
// silent-renew timeout mean "go interactive"; every other error is rethrown rather than silently
// swallowed, since a fresh, non-forced token request failing outright is not something this module
// has ever treated as "try a redirect and move on".
const handleInitialAcquireFailure = async (instance, error, tokenRequest, forceLoginRedirect) => {
  if (error instanceof InteractionRequiredAuthError) {
    try {
      await instance.acquireTokenRedirect(tokenRequest);
      return null;
    } catch (redirectError) {
      console.error('msal-token: acquireTokenRedirect failed', redirectError);
      if (!forceLoginRedirect) {
        throw redirectError;
      }
      return startThrottledLoginRedirectAndReturnNull(instance);
    }
  }

  if (isSilentRenewTimeout(error)) {
    if (!forceLoginRedirect) {
      // The caller explicitly asked not to redirect (mixedAuthLubaApi.js's prepareHeaders, so its
      // own customBaseQuery can drive the /gc/login redirect instead of MSAL's). Rethrowing here
      // used to rejct prepareHeaders' whole promise, which fetchBaseQuery has no recovery from -
      // every request through that API errored outright, with no redirect at all, guest-checkpoint
      // (cookie) users unaffected only because they have no MSAL account to ever reach this path.
      // Treat it like "no token" instead: log it and let the request go out without a bearer,
      // which the backend rejects as a 401 that the existing retry/redirect path already handles.
      console.error('msal-token: silent renewal timed out, continuing without a token', error);
      return null;
    }
    return startThrottledLoginRedirectAndReturnNull(instance);
  }

  throw error;
};

// Handles a failure of a FORCED refresh - either the caller's own explicit forceRefresh: true (a
// 401 retry, which already knows the previously cached token was rejected), or this module's own
// near-expiry-triggered retry once the cached token has been confirmed to have no time left
// either (see acquireIdTokenWithInstance's isExpired check, which handles the "not actually
// expired yet" case before ever calling this). Unlike handleInitialAcquireFailure, ANY failure
// here means silent renewal genuinely isn't working right now, so this never rethrows an
// unrecognised error - the only choice left is an interactive login, unless the caller explicitly
// asked not to redirect (forceLoginRedirect: false - see services/mixedAuthLubaApi.js, which
// handles its own /gc/login redirect instead of MSAL's).
const handleForcedRefreshFailure = async (instance, error, tokenRequest, forceLoginRedirect) => {
  if (error instanceof InteractionRequiredAuthError) {
    try {
      await instance.acquireTokenRedirect(tokenRequest);
      return null;
    } catch (redirectError) {
      console.error('msal-token: acquireTokenRedirect failed', redirectError);
      // fall through to the throttled loginRedirect below regardless of forceLoginRedirect - a
      // forced refresh only runs because silent renewal already isn't returning a usable token, so
      // there is nothing left to hand back to the caller either way.
    }
  } else if (!forceLoginRedirect) {
    throw error;
  }

  return startThrottledLoginRedirectAndReturnNull(instance);
};

/**
 * Instance-based ID-token acquisition, ported from lis-paasla's fix/task-123-kiosk-token
 * (msal-utils.js's acquireAccessTokenWithInstance, commits d2514ed and afb7398) but keeping
 * lis-front's existing null-return contract instead of paasla's throwing AuthRedirectError -
 * callers across lis-front already treat a null token as "not signed in / redirecting", see
 * msal-utils.js's original acquireAccessToken.
 *
 * - no account -> null (starting a loginRedirect first, unless forceLoginRedirect is false).
 * - forceRefresh: true (an explicit 401 retry) -> a single forced acquireTokenSilent; any failure
 *   goes straight to handleForcedRefreshFailure (there is no cached fallback here - the caller
 *   just had that exact cached token rejected).
 * - otherwise, acquireTokenSilent(scopes, account); if the returned (or cached account's)
 *   idTokenClaims.exp is within ID_TOKEN_REFRESH_MARGIN_MS of now, silently refreshes again with
 *   forceRefresh: true - see isIdTokenNearExpiry's comment for why that check is needed at all. If
 *   that forced refresh itself fails, and the token acquired a moment ago hasn't actually passed
 *   its exp yet (still inside the margin, not past it), that still-valid token is returned instead
 *   of failing/redirecting over what may just be a transient blip in this proactive refresh.
 * - InteractionRequiredAuthError / a silent-renew timeout -> a throttled interactive login,
 *   resolving null either way; any other error from the initial acquire is rethrown, but any other
 *   error from a forced refresh also goes interactive (see handleForcedRefreshFailure).
 */
export const acquireIdTokenWithInstance = async (instance, options = {}) => {
  const { forceRefresh = false, forceLoginRedirect = true } = options;
  const account = getAccount(instance);

  if (!account) {
    if (forceLoginRedirect) {
      try {
        await instance.loginRedirect({ scopes: config.AUTH.loginRequest.scopes });
      } catch (redirectError) {
        console.error('msal-token: login redirect failed', redirectError);
      }
    }
    return null;
  }

  const tokenRequest = {
    scopes: config.AUTH.loginRequest.scopes,
    account,
  };

  if (forceRefresh) {
    let response;
    try {
      response = await acquireTokenSilent(instance, tokenRequest, true);
    } catch (error) {
      return handleForcedRefreshFailure(instance, error, tokenRequest, forceLoginRedirect);
    }

    if (response?.account) {
      instance.setActiveAccount(response.account);
    }
    return response?.idToken || null;
  }

  let response;
  try {
    response = await acquireTokenSilent(instance, tokenRequest, false);
  } catch (error) {
    return handleInitialAcquireFailure(instance, error, tokenRequest, forceLoginRedirect);
  }

  const needsRefresh = isIdTokenNearExpiry(response?.idTokenClaims) || isIdTokenNearExpiry(account?.idTokenClaims);

  if (needsRefresh) {
    try {
      response = await acquireTokenSilent(instance, tokenRequest, true);
    } catch (refreshError) {
      const stillValid = !isExpired(response?.idTokenClaims) && !isExpired(account?.idTokenClaims);
      if (!stillValid) {
        return handleForcedRefreshFailure(instance, refreshError, tokenRequest, forceLoginRedirect);
      }
      // else: keep the existing `response` - it's inside the near-expiry margin but not yet
      // actually past its exp, so it's still safe to use while this proactive refresh is failing.
    }
  }

  if (response?.account) {
    instance.setActiveAccount(response.account);
  }

  return response?.idToken || null;
};
